import JSZip from 'jszip';
import { supabase } from '@/integrations/supabase/client';

export interface ExtractedCanvaExport {
  html: string;
  images: Array<{
    originalPath: string;
    publicUrl: string;
  }>;
}

export interface CanvaZipError {
  type: 'invalid_zip' | 'no_html' | 'upload_failed';
  message: string;
}

/**
 * Extract Canva ZIP export, upload images to Supabase storage,
 * and return HTML with image URLs replaced.
 */
export async function extractCanvaZip(
  zipFile: File,
  businessId: string
): Promise<{ success: true; data: ExtractedCanvaExport } | { success: false; error: CanvaZipError }> {
  try {
    // Load and parse ZIP file
    const zip = await JSZip.loadAsync(zipFile);

    // Find HTML file (usually email.html or index.html)
    let htmlContent: string | null = null;
    let htmlFileName: string | null = null;

    for (const fileName of Object.keys(zip.files)) {
      if (fileName.endsWith('.html') && !fileName.includes('/')) {
        const file = zip.files[fileName];
        if (!file.dir) {
          htmlContent = await file.async('text');
          htmlFileName = fileName;
          break;
        }
      }
    }

    if (!htmlContent) {
      return {
        success: false,
        error: {
          type: 'no_html',
          message: 'No HTML file found in the ZIP. Make sure you exported the email as HTML from Canva.'
        }
      };
    }

    // Find and upload images
    const uploadedImages: ExtractedCanvaExport['images'] = [];
    const timestamp = Date.now();

    for (const fileName of Object.keys(zip.files)) {
      const file = zip.files[fileName];

      // Check if it's an image file
      if (file.dir) continue;
      if (!isImageFile(fileName)) continue;

      try {
        // Get file content as blob
        const blob = await file.async('blob');

        // Generate unique filename for storage
        const extension = fileName.split('.').pop() || 'png';
        const safeName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
        const storagePath = `canva-emails/${businessId}/${timestamp}_${safeName}`;

        // Upload to Supabase storage
        const { error: uploadError } = await supabase.storage
          .from('business-assets')
          .upload(storagePath, blob, {
            cacheControl: '31536000', // 1 year cache
            upsert: true
          });

        if (uploadError) {
          console.error(`Failed to upload image ${fileName}:`, uploadError);
          continue;
        }

        // Get public URL
        const { data: urlData } = supabase.storage
          .from('business-assets')
          .getPublicUrl(storagePath);

        if (urlData?.publicUrl) {
          uploadedImages.push({
            originalPath: fileName,
            publicUrl: urlData.publicUrl
          });
        }
      } catch (err) {
        console.error(`Error processing image ${fileName}:`, err);
      }
    }

    // Replace image paths in HTML
    let processedHtml = htmlContent;
    for (const image of uploadedImages) {
      // Handle various path formats in HTML
      const originalPathPatterns = [
        image.originalPath,
        `./${image.originalPath}`,
        `/${image.originalPath}`,
        // Handle nested paths like "images/xxx.png"
        image.originalPath.split('/').pop() || ''
      ].filter(Boolean);

      for (const pattern of originalPathPatterns) {
        // Replace in src attributes
        const srcRegex = new RegExp(`src=["']([^"']*${escapeRegExp(pattern)})["']`, 'gi');
        processedHtml = processedHtml.replace(srcRegex, `src="${image.publicUrl}"`);

        // Replace in url() CSS
        const urlRegex = new RegExp(`url\\(["']?([^"'\\)]*${escapeRegExp(pattern)})["']?\\)`, 'gi');
        processedHtml = processedHtml.replace(urlRegex, `url("${image.publicUrl}")`);
      }
    }

    return {
      success: true,
      data: {
        html: processedHtml,
        images: uploadedImages
      }
    };

  } catch (error) {
    console.error('Error extracting Canva ZIP:', error);
    return {
      success: false,
      error: {
        type: 'invalid_zip',
        message: 'Failed to read the ZIP file. Make sure it\'s a valid Canva HTML export.'
      }
    };
  }
}

/**
 * Check if a filename is an image file
 */
function isImageFile(fileName: string): boolean {
  const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg'];
  const lowerName = fileName.toLowerCase();
  return imageExtensions.some(ext => lowerName.endsWith(ext));
}

/**
 * Escape special regex characters in a string
 */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
